import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { basicHeaders, legacyFulfill } from './utils';
import {
  CONFIG_RECEIVED_GET_CHARGER_CONFIG,
  CONFIG_RECEIVED_GET_CHARGER_CONFIGS,
  CONFIG_RECEIVED_GET_GATEWAY_CONFIGS,
  CONFIG_RECEIVED_GET_GATEWAY_CONFIG,
  CONFIG_RECEIVED_UPLOAD_VERSION_FILE,
  CONFIG_RECEIVED_GET_VERSIONS_META,
  CONFIG_RECEIVED_DELETE_VERSIONS_META,
  CONFIG_RECEIVED_GET_CHARGER_ENCRYPTED_PACKAGE,
  CONFIG_RECEIVED_UPDATE_CHARGER_FACTORY,
  CONFIG_RECEIVED_UPDATE_CHARGER_USER,
  CONFIG_RECEIVED_GET_GATEWAY_ENCRYPTED_PACKAGE,
  CONFIG_RECEIVED_UPDATE_GATEWAY_FACTORY,
  CONFIG_RECEIVED_UPDATE_GATEWAY_USER,
  CONFIG_RECEIVED_GET_BATTERY_CONFIGS,
  CONFIG_RECEIVED_GET_BATTERY_CONFIG,
  CONFIG_RECEIVED_UPDATE_BATTERY_FACTORY,
  CONFIG_RECEIVED_UPDATE_BATTERY_USER,
  CONFIG_RECEIVED_GET_VANADIUM_BESS_CONFIGS,
  CONFIG_RECEIVED_GET_VANADIUM_BESS_CONFIG,
  CONFIG_RECEIVED_UPDATE_VANADIUM_BESS_FACTORY,
  CONFIG_RECEIVED_UPDATE_VANADIUM_BESS_USER,
  CONFIG_RECEIVED_GET_LEADACID_BESS_CONFIGS,
  CONFIG_RECEIVED_GET_LEADACID_BESS_CONFIG,
  CONFIG_RECEIVED_UPDATE_LEADACID_BESS_FACTORY,
  CONFIG_RECEIVED_UPDATE_LEADACID_BESS_USER,
  CONFIG_RECEIVED_GET_DEVICE_CONFIG,
} from '../legacy/actions/config-actions';
import { portalApi } from './portal/portalApi';
import { portalCacheTags } from './portal/portalApiHelpers';
import { SimulatorEvent } from '../pages/SimulatorPage/SimulatorEventTimeline.types';

export interface BatterySettingsResponse {
  batteries: Battery[];
}

interface Battery {
  batterySerial: string;
  config: Config;
}

interface FactoryHistory {
  userName: string;
  createdAt: string;
  factory: Factory2;
}

interface Factory2 {
  pcb_x_addr: string;
  seq_count: number;
  asset_serial_number?: string;
  name?: string;
  version?: number;
  bms_type?: number;
  bms_serial_number?: string;
  bms_mac_address?: string;
  model_number?: string;
  manufacturer?: number;
  voltage?: number;
  capacity?: number;
  service_entry_date?: string;
  battery_type?: number;
  cable_configuration?: number;
  truck_name?: string;
  truck_type?: string;
  charger_type?: number;
  idle_current_threshold?: number;
  truck_id_present?: boolean;
  sim_type?: number;
  target_server?: number;
  debug_data_enabled?: boolean;
  battery_sub_type?: number;
  charge_start_rate?: number;
  charge_profile?: number;
}

interface User {}

interface Factory {
  seq_count: number;
  version: number;
  bms_type: number;
  bms_serial_number: string;
  bms_mac_address: string;
  name: string;
  asset_serial_number: string;
  model_number: string;
  manufacturer: number;
  voltage: number;
  capacity: number;
  service_entry_date: string;
  battery_type: number;
  cable_configuration: number;
  truck_name: string;
  truck_type: string;
  charger_type: number;
  idle_current_threshold: number;
  truck_id_present: boolean;
  sim_type: number;
  target_server: number;
  debug_data_enabled: boolean;
  battery_sub_type: number;
  charge_start_rate: number;
  charge_profile: number;
  pcb_x_addr: string;
}

export interface SettingsResponse {
  chargers: Charger[];
}

export interface GatewaySettingsResponse {
  gateways: GatewaySettings[];
}

export interface GatewaySettings {
  gatewaySerial: string;
  config: Config;
}

export interface SimulatorUpsertRequest {
  assetId: number | string;
  gatewaySerial?: string;
  schema: string;
  schemaVersion: string;
  startTime?: Date;
  version?: string;
  events: SimulatorEvent[];
  active: boolean;
}

export interface SimulatorResponse extends SimulatorUpsertRequest {
  batterySerial: string;
}

export interface SimulatorIndexResponse {
  simulators: SimulatorResponse[];
}

interface Charger {
  chargerSerial: string;
  config: Config;
}

export interface Config {
  factory: Factory;
  factoryHistory: FactoryHistory[];
  user: User;
  userHistory: UserHistory[];
}

interface UserHistory {
  userName: string;
  createdAt: string;
  user: User2;
  chargerSerial?: string;
}

interface User2 {
  name: null | string;
  seq_count?: number;
  version?: number;
  profile?: number;
  profile_std?: number;
  arc_warn?: number;
  warn_secs?: number;
  ignore_arc_warn_for_mod?: boolean;
  bat_comms?: number;
  std_rate?: number;
  autostart?: boolean;
  end_soc?: number;
  time_zone?: string;
  wifi_ssid?: string;
  wifi_pw?: string;
  wifi_hidden?: boolean;
  ip_static?: boolean;
  ip_address?: string;
  ip_mask?: string;
  ip_gateway?: string;
  ip_dns?: string;
  bat_over_temp?: number;
  li_cb_start_lo_temp_c?: number;
  li_cb_max_lo_temp_c?: number;
  li_err_lo_temp_c?: number;
  li_cb_start_hi_temp_c?: number;
  li_cb_max_hi_temp_c?: number;
  li_err_hi_temp_c?: number;
  enable_eq_btn?: boolean;
  oc_feature?: boolean;
  auto_balance?: boolean;
  start_rate?: number;
  start_rate_std?: number;
  v_fin?: number;
  v_cv?: number;
  cc_timeout?: number;
  cv_timeout?: number;
  mix_timeout?: number;
  fin_timeout?: number;
  eq_timeout?: number;
  volt_temp_comp?: number;
  v24_ahrs?: number;
  v24_max_amps?: number;
  v36_ahrs?: number;
  v36_max_amps?: number;
  v48_ahrs?: number;
  v48_max_amps?: number;
  v72_ahrs?: number;
  v72_max_amps?: number;
  v80_ahrs?: number;
  v80_max_amps?: number;
  fin_eq_schedule?: string;
  fin_makeup_schedule?: string;
  mix_schedule?: string;
  min_green_soc?: number;
  ui_unreachable?: boolean;
  contact_name?: string;
  contact_num?: string;
  h2o_enabled?: boolean;
  h2o_seconds?: number;
  schedule_shortcuts?: string;
  suppress_electrolyte?: boolean;
  autostart_seconds?: number;
  autostart_beep?: boolean;
  auto_eq?: boolean;
}

interface User {
  seq_count: number;
  version: number;
  name: string;
  profile: number;
  profile_std: number;
  arc_warn: number;
  warn_secs: number;
  ignore_arc_warn_for_mod: boolean;
  bat_comms: number;
  std_rate: number;
  autostart: boolean;
  autostart_seconds: number;
  autostart_beep: boolean;
  end_soc: number;
  time_zone: string;
  wifi_ssid: string;
  wifi_pw: string;
  wifi_hidden: boolean;
  ip_static: boolean;
  ip_address: string;
  ip_mask: string;
  ip_gateway: string;
  ip_dns: string;
  bat_over_temp: number;
  li_cb_start_lo_temp_c: number;
  li_cb_max_lo_temp_c: number;
  li_err_lo_temp_c: number;
  li_cb_start_hi_temp_c: number;
  li_cb_max_hi_temp_c: number;
  li_err_hi_temp_c: number;
  enable_eq_btn: boolean;
  oc_feature: boolean;
  auto_balance: boolean;
  auto_eq: boolean;
  start_rate: number;
  start_rate_std: number;
  v_fin: number;
  v_cv: number;
  cc_timeout: number;
  cv_timeout: number;
  mix_timeout: number;
  fin_timeout: number;
  eq_timeout: number;
  volt_temp_comp: number;
  v24_ahrs: number;
  v24_max_amps: number;
  v36_ahrs: number;
  v36_max_amps: number;
  v48_ahrs: number;
  v48_max_amps: number;
  v72_ahrs: number;
  v72_max_amps: number;
  v80_ahrs: number;
  v80_max_amps: number;
  fin_eq_schedule: string;
  fin_makeup_schedule: string;
  mix_schedule: string;
  min_green_soc: number;
  ui_unreachable: boolean;
  contact_name: string;
  contact_num: string;
  h2o_enabled: boolean;
  h2o_seconds: number;
  suppress_electrolyte: boolean;
}

interface FactoryHistory {
  userName: string;
  createdAt: string;
  factory: Factory2;
  chargerSerial?: string;
}

interface Factory2 {
  pcb_x_addr: string;
  charger_serial: string;
  version?: number;
  application_type?: number;
  charger_brand?: number;
  charger_serial_number?: string;
  wifi_mac?: string;
  ethernet?: boolean;
  bt_mac?: string;
  charger_type?: number;
  drawer_family?: number;
  drawer_variant?: number;
  model_number?: string;
  cabinet_type?: number;
  drawer_quantity?: number;
  hw_config?: number;
  charger_revision?: number;
  pcb_revision?: number;
  pcb_serial_number?: string;
  pcb_electronic_id?: string;
  max_output_voltage?: number;
  max_output_current?: number;
  cable_config?: number;
  release_level?: string;
  distribution_group?: string;
  cable_compensation?: number;
  dashboard_control_enable?: boolean;
  demo_control_enable?: boolean;
  charger_mode?: number;
  demo_drawer_enable?: boolean;
  alternate_upgrade?: boolean;
  can_debug?: boolean;
  map_buttons?: boolean;
  load_bank_enable?: boolean;
  dev_override_enable?: boolean;
  level?: number;
  remote_start_stop?: boolean;
  power_savings?: boolean;
  maintenance_mode?: boolean;
  cec_compliance?: boolean;
  sense_80v?: boolean;
  watchdog_alert?: number;
  dashboard_type?: number;
  disable_user_no_mod?: boolean;
  min_bat_v_threshold?: number;
  max_variance?: number;
  max_variance_s?: number;
  li_boost_v_main?: boolean;
  li_boost_v_eq?: boolean;
  li_boost_v_vh?: boolean;
  network_shortcuts?: string;
  min_charger_temp_c?: number;
  fury_cycler_can?: boolean;
  eth_dev?: boolean;
  eth_dev_host_address?: number;
  max_cable_i?: number;
  cable_qty?: number;
  conn_type?: number;
  wire_size?: number;
  cable_length?: number;
  conn_color?: number;
  light_stack?: boolean;
}

interface Factory {
  seq_count: number;
  version: number;
  application_type: number;
  charger_brand: number;
  charger_serial_number: string;
  wifi_mac: string;
  ethernet: boolean;
  eth_dev: boolean;
  eth_dev_host_address: number;
  bt_mac: string;
  charger_type: number;
  drawer_family: number;
  drawer_variant: number;
  model_number: string;
  cabinet_type: number;
  drawer_quantity: number;
  hw_config: number;
  charger_revision: number;
  pcb_revision: number;
  pcb_serial_number: string;
  pcb_electronic_id: string;
  max_output_voltage: number;
  max_output_current: number;
  min_charger_temp_c: number;
  max_cable_i: number;
  cable_qty: number;
  conn_type: number;
  wire_size: number;
  cable_length: number;
  conn_color: number;
  release_level: string;
  distribution_group: string;
  cable_compensation: number;
  dashboard_control_enable: boolean;
  demo_control_enable: boolean;
  charger_mode: number;
  demo_drawer_enable: boolean;
  alternate_upgrade: boolean;
  can_debug: boolean;
  fury_cycler_can: boolean;
  map_buttons: boolean;
  load_bank_enable: boolean;
  dev_override_enable: boolean;
  level: number;
  remote_start_stop: boolean;
  light_stack: boolean;
  power_savings: boolean;
  maintenance_mode: boolean;
  cec_compliance: boolean;
  sense_80v: boolean;
  watchdog_alert: number;
  dashboard_type: number;
  disable_user_no_mod: boolean;
  pcb_x_addr: string;
  min_bat_v_threshold: number;
  max_variance: number;
  max_variance_s: number;
  li_boost_v_main: boolean;
  li_boost_v_eq: boolean;
  li_boost_v_vh: boolean;
  cable_config: number;
  charger_serial: string;
}

type IsFlaggedRequest = {
  isThirdPartyCharger: boolean;
};

export const configApi = createApi({
  reducerPath: 'configApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_APP_CONFIG_SERVICE}/`,
    prepareHeaders: basicHeaders,
  }),
  tagTypes: [
    'chargers',
    'batteries',
    'devices',
    'assets',
    'gateways',
    'leadacid-bess',
    'vanadium-bess',
    'simulators',
  ],
  endpoints: (builder) => ({
    // prettier-ignore
    getChargerSettings: builder.query<SettingsResponse,{ ids: string[]; showHistory: boolean }>({
      query: ({ ids, showHistory }) => ({
        url: "/chargers/settings?showHistory=" + showHistory,
        method: "POST",
        body: {ids},
      }),
      providesTags: ['chargers'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_CHARGER_CONFIGS)
    }),
    getDeviceConfig: builder.query<Config, { device: string }>({
      query: ({ device }) => ({
        url: `/device-configs/${device}`,
        method: 'GET',
      }),
      providesTags: ['devices'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_DEVICE_CONFIG),
    }),
    getGatewayConfigs: builder.query<GatewaySettingsResponse, { ids: string[]; showHistory: boolean }>({
      query: ({ ids, showHistory }) => ({
        url: '/gateways/settings?showHistory=' + showHistory,
        method: 'POST',
        body: { ids },
      }),
      providesTags: ['gateways'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_GATEWAY_CONFIGS),
    }),
    getAccountGatewayConfigs: builder.query<SettingsResponse, number>({
      query: (accountId) => ({
        url: `/gateways/settings/${accountId}`,
        method: 'GET',
      }),
      providesTags: ['gateways'],
    }),
    getGatewayConfig: builder.query<Config, { id: string }>({
      query: ({ id }) => ({
        url: '/gateways/settings?showHistory=true',
        method: 'POST',
        body: { ids: [id] },
      }),
      providesTags: ['gateways'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_GATEWAY_CONFIG),
    }),
    uploadVersionFile: builder.mutation<any, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('fileName', file.name);
        return {
          url: '/versions/upload',
          method: 'POST',
          body: formData,
        };
      },
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPLOAD_VERSION_FILE),
    }),
    getVersionMetas: builder.query<any, void>({
      query: () => ({
        url: '/versions',
        method: 'GET',
      }),
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_VERSIONS_META),
    }),
    deleteVersionMeta: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/versions/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_DELETE_VERSIONS_META),
    }),
    getEncryptedChargerPackage: builder.query<any, { chargerSerial: string }>({
      query: ({ chargerSerial }) => ({
        url: `/chargers/encrypted-package/${chargerSerial}`,
        method: 'GET',
      }),
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_CHARGER_ENCRYPTED_PACKAGE),
    }),
    updateChargerFactorySettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/chargers/factory-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['chargers'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_CHARGER_FACTORY, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    updateChargerUserSettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/chargers/user-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['chargers'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_CHARGER_USER, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    getEncryptedGatewayPackage: builder.query<any, { gatewaySerial: string }>({
      query: ({ gatewaySerial }) => ({
        url: `/gateways/encrypted-package/${gatewaySerial}`,
        method: 'GET',
      }),
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_GATEWAY_ENCRYPTED_PACKAGE),
    }),
    updateGatewayFactorySettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/gateways/factory-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['gateways'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_GATEWAY_FACTORY, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    updateGatewayUserSettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/gateways/user-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['gateways'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_GATEWAY_USER, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    getBatteryConfigs: builder.query<SettingsResponse, { ids: string[]; showHistory: boolean }>({
      query: ({ ids, showHistory }) => ({
        url: '/batteries/settings?showHistory=' + showHistory,
        method: 'POST',
        body: { ids },
      }),
      providesTags: ['batteries'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_BATTERY_CONFIGS),
    }),
    getBatteryConfig: builder.query<Config, { id: string }>({
      query: ({ id }) => ({
        url: '/batteries/settings?showHistory=true',
        method: 'POST',
        body: { ids: [id] },
      }),
      providesTags: ['batteries'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_BATTERY_CONFIG),
    }),
    updateBatterySeicOptions: builder.mutation<any, { serial: string; flags: IsFlaggedRequest }>({
      query: ({ serial, flags }) => ({
        url: `/batteries/${serial}/application-options`,
        method: 'PUT',
        // Keep payload strict to current backend contract; expand here when backend adds flags.
        body: {
          isThirdPartyCharger: flags.isThirdPartyCharger,
        },
      }),
      invalidatesTags: ['batteries'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_BATTERY_FACTORY, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    updateBatteryFactorySettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/batteries/factory-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['batteries'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_BATTERY_FACTORY, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    updateBatteryUserSettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/batteries/user-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['batteries'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_BATTERY_USER, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    getVanadiumBESSConfigs: builder.query<
      SettingsResponse,
      { ids: string[]; showHistory: boolean }
    >({
      query: ({ ids, showHistory }) => ({
        url: '/vanadium-bess/settings?showHistory=' + showHistory,
        method: 'POST',
        body: { ids },
      }),
      providesTags: ['vanadium-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_VANADIUM_BESS_CONFIGS),
    }),
    getVanadiumBESSConfig: builder.query<Config, { id: string }>({
      query: ({ id }) => ({
        url: '/vanadium-bess/settings?showHistory=true',
        method: 'POST',
        body: { ids: [id] },
      }),
      providesTags: ['vanadium-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_VANADIUM_BESS_CONFIG),
    }),
    updateVanadiumBESSFactorySettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/vanadium-bess/factory-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['vanadium-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_VANADIUM_BESS_FACTORY, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    updateVanadiumBESSUserSettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/vanadium-bess/user-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['vanadium-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_VANADIUM_BESS_USER, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    getLeadAcidBESSConfigs: builder.query<
      SettingsResponse,
      { ids: string[]; showHistory: boolean }
    >({
      query: ({ ids, showHistory }) => ({
        url: '/leadacid-bess/settings?showHistory=' + showHistory,
        method: 'POST',
        body: { ids },
      }),
      providesTags: ['leadacid-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_LEADACID_BESS_CONFIGS),
    }),
    getLeadAcidBESSConfig: builder.query<Config, { id: string }>({
      query: ({ id }) => ({
        url: '/leadacid-bess/settings?showHistory=true',
        method: 'POST',
        body: { ids: [id] },
      }),
      providesTags: ['leadacid-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_GET_LEADACID_BESS_CONFIG),
    }),
    updateLeadAcidBESSFactorySettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/leadacid-bess/factory-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['leadacid-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_LEADACID_BESS_FACTORY, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    updateLeadAcidBESSUserSettings: builder.mutation<any, { settings: any; serial: string }>({
      query: ({ settings, serial }) => ({
        url: `/leadacid-bess/user-settings/${serial}`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['leadacid-bess'],
      onQueryStarted: legacyFulfill(CONFIG_RECEIVED_UPDATE_LEADACID_BESS_USER, (dispatch) => {
        dispatch(portalApi.util.invalidateTags([portalCacheTags.ASSETS]));
      }),
    }),
    createSimulator: builder.mutation<SimulatorResponse, { request: SimulatorUpsertRequest }>({
      query: ({ request }) => ({
        url: `/simulators/${request.assetId}`,
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: ['simulators'],
    }),
    updateSimulator: builder.mutation<SimulatorResponse, { request: SimulatorUpsertRequest }>({
      query: ({ request }) => ({
        url: `/simulators/${request.assetId}`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['simulators'],
    }),
    getSimulator: builder.query<SimulatorResponse, string>({
      query: (assetId) => ({
        url: `/simulators/${assetId}`,
        method: 'GET',
      }),
      providesTags: (result, error, assetId) => [{ type: 'simulators' as const, id: assetId }],
    }),
    getSimulatorConfigs: builder.query<SimulatorIndexResponse, void>({
      query: () => ({
        url: '/simulators',
        method: 'GET',
      }),
      providesTags: ['simulators'],
    }),
    getSimulatorConfigByDeviceId: builder.query<SimulatorResponse, string>({
      query: (batterySerial) => ({
        url: `/simulators/deviceId/${batterySerial}`,
        method: 'GET',
      }),
      providesTags: (result, error, batterySerial) => [
        { type: 'simulators' as const, id: batterySerial },
      ],
    }),
    deleteSimulator: builder.mutation<void, string>({
      query: (batterySerial) => ({
        url: `/simulators/${batterySerial}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['simulators'],
    }),
    getAssetListConfigs: builder.query<
      SettingsResponse,
      {
        ids: string[];
        type: 'batteries' | 'gateways' | 'chargers' | 'vanadiumBESS' | 'leadacidBESS';
      }
    >({
      query: ({ ids, type }) => ({
        url: `/list/${type}`,
        method: 'PUT',
        body: { ids, type },
      }),
    }),
  }),
});

export const {
  useGetChargerSettingsQuery,
  useLazyGetChargerSettingsQuery,
  useGetDeviceConfigQuery,
  useLazyGetDeviceConfigQuery,
  useGetGatewayConfigsQuery,
  useLazyGetGatewayConfigsQuery,
  useGetGatewayConfigQuery,
  useLazyGetGatewayConfigQuery,
  useUploadVersionFileMutation,
  useGetVersionMetasQuery,
  useLazyGetVersionMetasQuery,
  useDeleteVersionMetaMutation,
  useGetEncryptedChargerPackageQuery,
  useLazyGetEncryptedChargerPackageQuery,
  useUpdateChargerFactorySettingsMutation,
  useUpdateChargerUserSettingsMutation,
  useGetEncryptedGatewayPackageQuery,
  useLazyGetEncryptedGatewayPackageQuery,
  useUpdateGatewayFactorySettingsMutation,
  useUpdateGatewayUserSettingsMutation,
  useGetBatteryConfigsQuery,
  useLazyGetBatteryConfigsQuery,
  useGetBatteryConfigQuery,
  useLazyGetBatteryConfigQuery,
  useUpdateBatterySeicOptionsMutation,
  useUpdateBatteryFactorySettingsMutation,
  useUpdateBatteryUserSettingsMutation,
  useGetVanadiumBESSConfigsQuery,
  useLazyGetVanadiumBESSConfigsQuery,
  useGetVanadiumBESSConfigQuery,
  useLazyGetVanadiumBESSConfigQuery,
  useUpdateVanadiumBESSFactorySettingsMutation,
  useUpdateVanadiumBESSUserSettingsMutation,
  useGetLeadAcidBESSConfigsQuery,
  useLazyGetLeadAcidBESSConfigsQuery,
  useGetLeadAcidBESSConfigQuery,
  useLazyGetLeadAcidBESSConfigQuery,
  useUpdateLeadAcidBESSFactorySettingsMutation,
  useUpdateLeadAcidBESSUserSettingsMutation,
  useGetAccountGatewayConfigsQuery,
  useGetAssetListConfigsQuery,
  useLazyGetAssetListConfigsQuery,
  useCreateSimulatorMutation,
  useUpdateSimulatorMutation,
  useGetSimulatorQuery,
  useLazyGetSimulatorQuery,
  useGetSimulatorConfigsQuery,
  useLazyGetSimulatorConfigsQuery,
  useDeleteSimulatorMutation,
  useGetSimulatorConfigByDeviceIdQuery,
} = configApi;
